/**
 * @fileoverview Input/output schemas for divergence-report-short-v1.
 *
 * Short-form variant: no storyline or beat-level analysis.
 * Focuses on plot drift, character arc drift, and plot thread status
 * within a single-chapter story.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 * Reports are ephemeral — not persisted to project state.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Short-form: plannedPlot + shortFormDraftContext + characterDivergenceData + plotThreadStatus.
 * No storylineDivergenceData or actualPlotProgression (those are multi-chapter concepts).
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.plannedPlot.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.shortFormDraftContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characterDivergenceData.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.plotThreadStatus.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

const PlotDivergenceSchema = z.object({
  type: z.enum(['planned_not_occurred', 'unplanned_occurred', 'reordered', 'modified', 'tone_shift']).describe(
    'The type of plot divergence.'
  ),
  description: z.string().describe('What diverged — specific and concrete.'),
  significance: z.enum(['moderate', 'major']).describe('How impactful this divergence is.'),
  recommendation: z.enum(['update_plan', 'revise_draft', 'author_decision']).describe(
    'Suggested action: update the outline to match the draft, revise the draft to match the outline, or leave it to the author.'
  ),
});

const CharacterDivergenceSchema = z.object({
  characterName: z.string().describe('The name of the character whose arc diverged.'),
  type: z.enum(['arc_diverged', 'role_shifted', 'absent', 'elevated']).describe(
    'The type of character arc divergence.'
  ),
  description: z.string().describe('What specifically diverged in this character\'s arc.'),
  significance: z.enum(['moderate', 'major']).describe('How impactful this divergence is.'),
});

const ThreadDivergenceSchema = z.object({
  threadTitle: z.string().describe('The title of the plot thread.'),
  type: z.enum(['unresolved', 'premature_resolution', 'lost_thread', 'unplanned_thread']).describe(
    'The type of thread divergence.'
  ),
  description: z.string().describe('What specifically diverged with this thread.'),
});

/**
 * Output schema for divergence-report-short-v1.
 * Three sections: plot, character, threads. No storyline section.
 * Only moderate and major significance — no cosmetic/minor items.
 */
export const OutputSchema = z.object({
  summary: z.object({
    verdict: z.enum(['on_track', 'minor_drift', 'significant_drift', 'major_divergence']).describe(
      'Overall assessment of how far the written draft has diverged from the outline.'
    ),
    overview: z.string().describe('A 2-3 sentence summary of the divergence state.'),
  }),

  plotDivergences: z.array(PlotDivergenceSchema).describe(
    'Plot-level divergences between the outline and the written draft. Only include significant items.'
  ),

  characterDivergences: z.array(CharacterDivergenceSchema).describe(
    'Per-character arc divergences: arcs that went differently than planned. Only include significant items.'
  ),

  threadDivergences: z.array(ThreadDivergenceSchema).describe(
    'Plot thread issues: threads left unresolved, resolved too early, lost, or unplanned.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type PlotDivergenceShort = z.infer<typeof PlotDivergenceSchema>;
export type CharacterDivergenceShort = z.infer<typeof CharacterDivergenceSchema>;
export type ThreadDivergenceShort = z.infer<typeof ThreadDivergenceSchema>;
